import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IExistingFeedEntryPenDetail,
    IFeedEntryPenDetail,
    IFeedEntryPenDetailError,
    IFeedEntryRationDetails,
    IFeedEntryRationDetailsError,
    ILotPenFeedDistribution,
    ILots,
    IPens,
    IRations,
} from '../../utils/Interface/ManualFeedEntryInterface';
import { MANUAL_FEED_ENTRY_INITIAL_STATE } from '../Constants/manualFeedEntryInitialState';
import { FeedDistributionScenarios } from '../../apps/feedlotManager/modules/feed/manualFeedEntry/interfaces';
import { mapFeedingPropertyToErrorProperty } from '../../apps/feedlotManager/modules/feed/manualFeedEntry/hooks/feedDistributionHooks';

const checkIfFeedIsValid = (pen, feedingHeadCountKey: string) => {
    return (
        !!pen[feedingHeadCountKey] ===
        pen.lotPenFeedDistributions.some(
            distribution => distribution[feedingHeadCountKey],
        )
    );
};

const checkDistributionErrors = (
    pen: IExistingFeedEntryPenDetail,
    feedingHeadCountKey: string,
) => {
    const feedIsValid = checkIfFeedIsValid(pen, feedingHeadCountKey);
    if (feedIsValid) {
        const errorProperty =
            mapFeedingPropertyToErrorProperty(feedingHeadCountKey);
        pen[errorProperty] = false;
    }
    return pen;
};

export const ManualFeedEntry = createSlice({
    name: 'ManualFeedEntry',
    initialState: { ...MANUAL_FEED_ENTRY_INITIAL_STATE },
    reducers: {
        setManualFeedEntryLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setLots: (state, action: PayloadAction<ILots[]>) => {
            state.lots = action.payload;
        },
        setRations: (state, action: PayloadAction<IRations[]>) => {
            state.rations = action.payload;
        },
        setPens: (state, action: PayloadAction<IPens[]>) => {
            state.pens = action.payload;
        },
        setManualFeedEntryRationDetails: (
            state,
            action: PayloadAction<IFeedEntryRationDetails>,
        ) => {
            state.feedEntryRationDetails = action.payload;
        },
        setManualFeedEntryRationDetailsError: (
            state,
            action: PayloadAction<IFeedEntryRationDetailsError>,
        ) => {
            state.feedEntryRationDetailsError = action.payload;
        },
        setManualFeedEntryPensDetails: (
            state,
            action: PayloadAction<IFeedEntryPenDetail[]>,
        ) => {
            state.feedEntryPensDetails = action.payload;
        },
        setManualFeedEntryExistingPensDetailsRow: (state, action) => {
            state.existingFeedEntryPensDetails[action.payload.index] =
                action.payload.data;
        },
        setManualFeedEntryExistingPensDetails: (
            state,
            action: PayloadAction<IExistingFeedEntryPenDetail[]>,
        ) => {
            state.existingFeedEntryPensDetails = action.payload;
        },
        setManualFeedEntryFetchedExistingPensDetails: (
            state,
            action: PayloadAction<IExistingFeedEntryPenDetail[]>,
        ) => {
            state.fetchedExistingFeedEntryPensDetails = action.payload;
        },
        setManualFeedEntryExistingPensDetailsError: (
            state,
            action: PayloadAction<IFeedEntryPenDetailError[]>,
        ) => {
            state.existingFeedEntryPensDetailsError = action.payload;
        },
        setShowEstimatedHdCountField: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showEstimatedHdCountField = action.payload;
        },
        setIsExistingEstHdCountModalReadonly: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.isExistingEstHdCountModalReadonly = action.payload;
        },
        setMfeSelectedPenIndex: (state, action: PayloadAction<number>) => {
            state.selectedPenIndex = action.payload;
        },
        setIsRecordSubmitted: (state, action: PayloadAction<boolean>) => {
            state.isRecordSubmitted = action.payload;
        },
        setSubmitPending: (state, action: PayloadAction<boolean>) => {
            state.submitPending = action.payload;
        },
        setIsIpadFeedUpdateValid: (state, action: PayloadAction<boolean>) => {
            state.isIpadFeedUpdateValid = action.payload;
        },
        updatePenDetail: (
            state,
            action: PayloadAction<IExistingFeedEntryPenDetail>,
        ) => {
            const penDetail = state.existingFeedEntryPensDetails.find(
                pd => pd.penId === action.payload.penId,
            );
            if (penDetail) {
                Object.assign(penDetail, action.payload);
            }
        },
        updateLotPenFeedDistributions: (
            state,
            action: PayloadAction<{
                penId: number;
                distributionScenario: FeedDistributionScenarios;
                lotPenFeedDistributions: ILotPenFeedDistribution[];
            }>,
        ) => {
            const { penId, distributionScenario, lotPenFeedDistributions } =
                action.payload;
            state.existingFeedEntryPensDetails.forEach(pen => {
                if (pen.penId === penId) {
                    pen.lotPenFeedDistributions = lotPenFeedDistributions;
                    pen.distributionScenario = distributionScenario;
                }
            });
        },
        updateDistributionHeadCount: (
            state,
            action: PayloadAction<{
                penId: number;
                distributionIndex: number;
                headcountForFeedingKey: string;
                headCount: number;
            }>,
        ) => {
            const {
                penId,
                distributionIndex,
                headcountForFeedingKey,
                headCount,
            } = action.payload;

            const penDetailIndex = state.existingFeedEntryPensDetails.findIndex(
                pen =>
                    pen.penId === penId && pen.lotPenFeedDistributions?.length,
            );

            if (
                penDetailIndex !== -1 &&
                state.existingFeedEntryPensDetails[penDetailIndex]
                    .lotPenFeedDistributions
            ) {
                state.existingFeedEntryPensDetails[
                    penDetailIndex
                ].lotPenFeedDistributions[distributionIndex][
                    headcountForFeedingKey
                ] = headCount;
                state.existingFeedEntryPensDetails[penDetailIndex] =
                    checkDistributionErrors(
                        state.existingFeedEntryPensDetails[penDetailIndex],
                        headcountForFeedingKey,
                    );
            }
        },
        toggleFeedDistributionTable: (state, action: PayloadAction<number>) => {
            const penDetail =
                state.existingFeedEntryPensDetails[action.payload];
            if (penDetail) {
                penDetail.showFeedDistribution =
                    !penDetail.showFeedDistribution;
            }
        },
        updateDistributionScenario: (
            state,
            action: PayloadAction<{
                penId: number;
                distributionScenario: FeedDistributionScenarios;
            }>,
        ) => {
            const { penId, distributionScenario } = action.payload;
            state.existingFeedEntryPensDetails.forEach(pen => {
                if (pen.penId === penId) {
                    pen.distributionScenario = distributionScenario;
                }
            });
        },
        updateLotPenFeedDistributionError: (
            state,
            action: PayloadAction<{
                penId: number;
                distributionIndex: number;
                errorProperty: string;
                hasError: boolean;
            }>,
        ) => {
            const { penId, distributionIndex, errorProperty, hasError } =
                action.payload;

            const penDetailIndex = state.existingFeedEntryPensDetails.findIndex(
                pen =>
                    pen.penId === penId && pen.lotPenFeedDistributions?.length,
            );

            if (
                penDetailIndex !== -1 &&
                state.existingFeedEntryPensDetails[penDetailIndex]
                    .lotPenFeedDistributions &&
                state.existingFeedEntryPensDetails[penDetailIndex]
                    .lotPenFeedDistributions[distributionIndex]
            ) {
                const distribution =
                    state.existingFeedEntryPensDetails[penDetailIndex]
                        .lotPenFeedDistributions[distributionIndex];

                if (!distribution.errors) {
                    distribution.errors = {
                        feed1DistributionHasError: false,
                        feed2DistributionHasError: false,
                        feed3DistributionHasError: false,
                        feed4DistributionHasError: false,
                    };
                }

                distribution.errors[errorProperty] = hasError;
            }
        },
    },
});

export const {
    setManualFeedEntryLoading,
    setLots,
    setRations,
    setPens,
    setManualFeedEntryExistingPensDetails,
    setManualFeedEntryFetchedExistingPensDetails,
    setManualFeedEntryExistingPensDetailsError,
    setManualFeedEntryRationDetails,
    setManualFeedEntryRationDetailsError,
    setManualFeedEntryPensDetails,
    setManualFeedEntryExistingPensDetailsRow,
    setShowEstimatedHdCountField,
    setIsExistingEstHdCountModalReadonly,
    setMfeSelectedPenIndex,
    setIsRecordSubmitted,
    setSubmitPending,
    setIsIpadFeedUpdateValid,
    updateLotPenFeedDistributions,
    toggleFeedDistributionTable,
    updateDistributionScenario,
    updatePenDetail,
    updateDistributionHeadCount,
    updateLotPenFeedDistributionError,
} = ManualFeedEntry.actions;

const manualFeedEntryReducer = ManualFeedEntry.reducer;
export default manualFeedEntryReducer;
